import { EeqCampaignOverridesResponse } from '@appTypes/essential-ui/EeqCampaignOverridesResponse';
import { Deal } from '@appTypes/header/Deal';
import { isDealerOptIn } from '@components/dealsheet/Helpers';
import { RecentOrFavorite } from '@enums/RecentOrFavorite';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { useDealType } from '@hooks/UseDealType';
import { getOfferedDealType } from '@services/api/GenericClient';
import { BrandsService } from '@services/shared/BrandsService';
import { DealsService } from '@services/shared/DealsService';
import { AppState } from '@store/AppState';
import { setIsDealsheetReload, setIsNotesOpen } from '@store/dealsheet/DealsheetActions';
import { DealsheetState } from '@store/dealsheet/DealsheetReducer';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  BpsFooter,
  BpsHeader,
  BpsWrapper,
  CustomerWrapper,
  DealInfoWrapper,
  DealPlaceholder,
  DealTypeWrapper,
  DetailWrapper,
  ListWrapper,
  MetaWrapper,
  NoResultContainer,
  NoResultWrapper,
  NotificationWrapper,
  Wrapper,
} from './RecentOrFavoriteDealsStyles';

type RecentOrFavoriteDealsProps = {
  dealType: RecentOrFavorite;
  isVisible: boolean;
  isIncentiveAdvantageType?: boolean;
  dealClickedEvent: () => void;
};

const RecentOrFavoriteDeals: FC<RecentOrFavoriteDealsProps> = ({
  dealType,
  isVisible,
  dealClickedEvent,
  isIncentiveAdvantageType,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recentOrFavouriteDeals, setRecentOrFavouriteDeals] = useState<Deal[]>([]);
  const { isMobileDevice } = useCheckDevice();
  const updateMakeName = useCallback((make: string): string => {
    return BrandsService.updateMakeName(make);
  }, []);

  const { getColorCode } = useDealType();
  const recentOrFavDealType = dealType;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isDealsheetReload } = useSelector<AppState, DealsheetState>(
    (state) => state.dealsheetModernized,
  );

  const goToDealsheet = (vin: string, closedDealId: string, dealerId: string, dealType: string) => {
    const recentOrFavoriteDeal = recentOrFavDealType;

    if (!isMobileDevice) {
      dealClickedEvent();
    } else {
      const lastNavigation = {
        state: recentOrFavoriteDeal === RecentOrFavorite.Recent ? 'app.recent' : 'app.favorites',
        route: dealType,
      };
      localStorage.setItem('lastNavigation', JSON.stringify(lastNavigation));
    }

    if (isIncentiveAdvantageType) {
      navigate(`/essential/dealsheet/${closedDealId}/${dealerId}`);
    } else {
      if (isDealerOptIn(dealerId)) {
        getOfferedDealType(dealerId, vin).then((response: EeqCampaignOverridesResponse) => {
          var offeredDealType = response?.offeredDealType;
          var urlDealType =
            offeredDealType !== undefined && offeredDealType !== null && offeredDealType !== ''
              ? offeredDealType.toLowerCase()
              : dealType;
          navigate(`/dealsheet/${vin}/${dealerId}/offer/1/${urlDealType}/bps`);
          dispatch(setIsDealsheetReload(!isDealsheetReload));
          dispatch(setIsNotesOpen(false));
        });
      } else {
        navigate(`/dealsheet/${vin}/${dealerId}/offer/1/${dealType}/bps`);
        dispatch(setIsDealsheetReload(!isDealsheetReload));
        dispatch(setIsNotesOpen(false));
      }
    }

    // Set up previous location for the back button to redirect user back to it
    // If user goes to loyalty deal sheet via notification from turn deal sheet
    // Turn deal sheet is set to be their last location for them to return to
    const currentURL = window.location.pathname;
    const urlParts = currentURL.split('/');
    const turnPath = urlParts[1] + '/' + urlParts[2];
    const lastNavigation = JSON.parse(localStorage.getItem('lastNavigation') ?? '');
    if (lastNavigation.state === 'app.turn.details' && turnPath !== 'turn/details') {
      const vin = urlParts[2];
      const dealerId = urlParts[3];
      const offerId = urlParts[5];
      const offerDealType = urlParts[6];
      const turnDealsheetRoute = `/dealsheet/${vin}/${dealerId}/offer/${offerId}/${offerDealType}/bps${location.search}`;
      const turnDealsheetNav = { state: 'app.turn.dealsheet', route: turnDealsheetRoute };
      localStorage.setItem('lastNavigation', JSON.stringify(turnDealsheetNav));
    }
  };

  useEffect(() => {
    if (isVisible) {
      setIsLoading(true);
      DealsService.getRecentOrFavorite(dealType === RecentOrFavorite.Favorite).then(
        (deals: Deal[]) => {
          setIsLoading(false);
          const uniqueDeals = deals.filter(({ vin }, index) => {
            return deals.findIndex((item) => item.vin === vin) === index;
          });
          setRecentOrFavouriteDeals(uniqueDeals);
        },
      );
    }
  }, [isVisible]);

  return (
    <Wrapper data-testid={`recent-or-favorite-deals`}>
      {recentOrFavouriteDeals?.length > 0 && (
        <ListWrapper isMobileDevice={isMobileDevice}>
          {recentOrFavouriteDeals?.map((deal: Deal) => {
            {
              return (
                <NotificationWrapper
                  onClick={() =>
                    goToDealsheet(
                      deal.vin,
                      deal.closedDealId,
                      deal.dealerId,
                      deal.dealType.toLowerCase(),
                    )
                  }
                  key={deal.vin}
                  data-testid={deal.vin}
                >
                  <DealInfoWrapper>
                    <CustomerWrapper>{deal.customerName}</CustomerWrapper>
                    <DetailWrapper>
                      {deal.year ?? ''} {updateMakeName(deal.make)} {deal.trim}
                    </DetailWrapper>
                  </DealInfoWrapper>
                  <MetaWrapper>
                    <DealTypeWrapper
                      backgroundColor={getColorCode(deal.dealType)}
                      title={`${deal.dealType} Deal`}
                    >
                      {deal.dealType.charAt(0)}
                    </DealTypeWrapper>
                    <BpsWrapper>
                      <BpsHeader>
                        BPS
                        <span>&reg;</span>
                      </BpsHeader>
                      <BpsFooter>{deal.bps > -1 ? deal.bps : 'N/A'}</BpsFooter>
                    </BpsWrapper>
                  </MetaWrapper>
                </NotificationWrapper>
              );
            }
          })}
        </ListWrapper>
      )}
      {recentOrFavouriteDeals?.length === 0 && (
        <NoResultWrapper
          isMobileDevice={isMobileDevice}
          data-testid={`no-${dealType.toLowerCase()}-deals`}
        >
          <NoResultContainer>
            <DealPlaceholder>
              {isLoading && <span>Loading...</span>}
              {!isLoading && <span>No {dealType} Deals</span>}
            </DealPlaceholder>
          </NoResultContainer>
        </NoResultWrapper>
      )}
    </Wrapper>
  );
};

export default RecentOrFavoriteDeals;
